/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    type ComponentPropsWithoutRef,
    type ReactElement,
} from 'react';
import { NavLink } from '@/components/link';
import { useNavigate } from '@/hooks/use-navigate';
import type { ShopperProducts } from '@/scapi';
import CategoryNavigationMenu, { WithCategoryNavigationMenu } from '@/components/navigation-menu';
import { Button } from '@/components/ui/button';
import { X, ChevronRight } from 'lucide-react';
import hamburgerIcon from '/images/hamburger.png';
import { useTranslation } from 'react-i18next';
import { LogoutConfirmDialog } from '@/components/header/user-actions/logout-confirm-dialog';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { NavigationMenuLink } from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { useSubCategory } from '@/components/navigation-menu/context';
import { routes, routeHref } from '@/route-paths';
import { useAuth } from '@/providers/auth';
import { toImageUrl, transformHtmlImageUrls } from '@/lib/images/dynamic-image';

interface MobileMenuContextType {
    isOpen: boolean;
    toggle: () => void;
    close: () => void;
    categories: ShopperProducts.schemas['Category'][];
}

const MobileMenuContext = createContext<MobileMenuContextType | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useMobileMenu() {
    return useContext(MobileMenuContext);
}

function hasBanner(category?: ShopperProducts.schemas['Category']): category is ShopperProducts.schemas['Category'] {
    return Boolean(
        (category as any)?.thumbnail ||
        category?.c_slotBannerImage ||
        (typeof category?.c_headerMenuBanner === 'string' && category.c_headerMenuBanner.length > 0)
    );
}

function isVertical(category?: ShopperProducts.schemas['Category']): category is ShopperProducts.schemas['Category'] {
    // Default to vertical if not set
    if (!category?.c_headerMenuOrientation) {
        return true;
    }
    // Only horizontal if explicitly set to "horizontal"
    return String(category.c_headerMenuOrientation).toLowerCase() !== 'horizontal';
}

function CategoryBanner({
    category,
    ...props
}: ComponentPropsWithoutRef<'a'> & { category: ShopperProducts.schemas['Category'] }) {
    const config = useConfig();
    const rawImage =
        ((category as any)?.thumbnail as string) ||
        (category?.c_slotBannerImage as string) ||
        (category?.image as string) ||
        '';
    const imageSrc = rawImage ? (toImageUrl({ src: rawImage, config }) ?? rawImage) : '';

    // Transform any image URLs in the HTML banner to use DIS with WebP optimization
    const transformedBannerHtml = transformHtmlImageUrls((category.c_headerMenuBanner as string) || '', config);

    return (
        <NavigationMenuLink asChild>
            <NavLink {...props} to={routeHref(routes.category, { categoryId: category.id })} className="!block !p-0 w-full h-full">
                {imageSrc ? (
                    <img
                        className="object-cover w-full h-full rounded-none"
                        src={imageSrc}
                        alt={category.name}
                    />
                ) : (
                    // eslint-disable-next-line react/no-danger
                    <div className="w-full h-full" dangerouslySetInnerHTML={{ __html: transformedBannerHtml }} />
                )}
            </NavLink>
        </NavigationMenuLink>
    );
}

function hasSubcategories(category: ShopperProducts.schemas['Category']): boolean {
    return (
        typeof category.onlineSubCategoriesCount === 'number' &&
        category.onlineSubCategoriesCount > 0 &&
        Array.isArray(category.categories) &&
        category.categories.length > 0
    );
}

function MobileMenuCategory({
    category: rawCategory,
    expandedCategories,
    onToggle,
    onNavigate,
}: {
    category: ShopperProducts.schemas['Category'];
    expandedCategories: Set<string>;
    onToggle: (categoryId: string) => void;
    onNavigate: () => void;
}): ReactElement {
    const { t } = useTranslation('header');
    const enrichedCategory = useSubCategory(rawCategory.id);
    const category = enrichedCategory ?? rawCategory;
    const hasChildren = hasSubcategories(category);
    const isExpanded = expandedCategories.has(category.id);

    const renderSubcategoryLinks = (
        subcategories: ShopperProducts.schemas['Category'][] | undefined,
        level = 1
    ): ReactElement[] =>
        subcategories?.map((subcategory) => (
            <li key={subcategory.id}>
                <NavLink
                    to={routeHref(routes.category, { categoryId: subcategory.id })}
                    onClick={onNavigate}
                    className={cn(
                        'block pt-2 pb-0 hover:opacity-70 transition-opacity text-[#000000]',
                        level > 1 && 'text-header-foreground/80'
                    )}
                    style={{
                        fontFamily: "'Georama SemiExpanded', 'Georama', sans-serif",
                        fontWeight: 400,
                        fontStyle: 'normal',
                        fontSize: '12px',
                        lineHeight: '18px',
                        letterSpacing: '0.04em',
                    }}>
                    {subcategory.name}
                </NavLink>
                {subcategory.categories?.length ? (
                    <ul className="pl-4 space-y-1">{renderSubcategoryLinks(subcategory.categories, level + 1)}</ul>
                ) : null}
            </li>
        )) ?? [];

    return (
        <li>
            <div className="flex items-center justify-between">
                <NavLink
                    to={routeHref(routes.category, { categoryId: category.id })}
                    onClick={onNavigate}
                    className="flex-1 py-3 hover:opacity-70 transition-opacity text-[#000000]"
                    style={{
                        fontFamily: "'Georama SemiExpanded', 'Georama', sans-serif",
                        fontWeight: 400,
                        fontStyle: 'normal',
                        fontSize: '12px',
                        lineHeight: '18px',
                        letterSpacing: '0.04em',
                    }}>
                    {category.name}
                </NavLink>

                {hasChildren ? (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onToggle(category.id)}
                        className="ml-2 py-2 pl-2 !pr-0 has-[>svg]:!pr-0 h-auto shrink-0 hover:bg-transparent hover:opacity-50 transition-opacity"
                        style={{ paddingRight: 0 }}
                        aria-label={
                            isExpanded
                                ? t('collapseCategory', {
                                      category: category.name,
                                      defaultValue: `Collapse ${category.name}`,
                                  })
                                : t('expandCategory', {
                                      category: category.name,
                                      defaultValue: `Expand ${category.name}`,
                                  })
                        }
                        aria-expanded={isExpanded}>
                        <ChevronRight
                            strokeWidth={1}
                            className={cn('size-4 text-[#000000] shrink-0 transition-transform duration-200', {
                                'rotate-90': isExpanded,
                            })}
                        />
                    </Button>
                ) : (
                    <NavLink
                        to={routeHref(routes.category, { categoryId: category.id })}
                        onClick={onNavigate}
                        className="ml-2 py-2 pl-2 !pr-0 shrink-0 flex items-center hover:opacity-70 transition-opacity"
                        style={{ paddingRight: 0 }}
                        aria-label={category.name}>
                        <ChevronRight className="size-4 text-[#000000] shrink-0" strokeWidth={1} />
                    </NavLink>
                )}
            </div>

            {hasChildren && isExpanded && (
                <ul className="pl-4 pb-0 space-y-1 border-l border-header-foreground/10">
                    {renderSubcategoryLinks(category.categories)}
                </ul>
            )}
        </li>
    );
}

/**
 * MobileMenuDropdown - Renders the mobile menu dropdown content with expandable subcategories
 * Uses absolute positioning (relative to header) to automatically appear below the header
 * regardless of header height changes. No hardcoded values needed.
 */
export function MobileMenuDropdown(): ReactElement | null {
    const context = useMobileMenu();
    const { t } = useTranslation('header');
    const session = useAuth();
    const isAuthenticated = Boolean(session?.userType === 'registered' && session?.customerId);
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
    const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

    useEffect(() => {
        if (!context?.isOpen) return;
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.body.dataset.mobileMenuOpen = 'true';
        return () => {
            document.body.style.overflow = originalOverflow;
            delete document.body.dataset.mobileMenuOpen;
        };
    }, [context?.isOpen]);

    if (!context) return null;

    const toggleCategory = (categoryId: string) => {
        setExpandedCategories((prev) => {
            const next = new Set(prev);
            if (next.has(categoryId)) {
                next.delete(categoryId);
            } else {
                next.add(categoryId);
            }
            return next;
        });
    };

    return (
        <>
            <div
                className={cn(
                    'lg:hidden absolute inset-x-0 left-0 right-0 top-full w-full',
                    'bg-header-background text-header-foreground shadow-lg z-40',
                    'h-[calc(100dvh-var(--header-height,0px))] overflow-y-auto',
                    { hidden: !context.isOpen }
                )}
                aria-hidden={!context.isOpen}>
                <nav
                    className="flex flex-col gap-6 p-4 pb-8"
                    aria-label={t('mobileNavigation', 'Mobile navigation menu')}>
                    <ul className="flex flex-col gap-1 w-full">
                        {context.categories.map((category) => (
                            <MobileMenuCategory
                                key={category.id}
                                category={category}
                                expandedCategories={expandedCategories}
                                onToggle={toggleCategory}
                                onNavigate={context.close}
                            />
                        ))}
                    </ul>

                    <div className="w-full h-0 border-t border-[#DFDFDF]" />

                    {isAuthenticated ? (
                        <div className="flex flex-col gap-6 w-full">
                            <NavLink
                                to={routes.account}
                                onClick={context.close}
                                className="flex items-center justify-between text-[#000000] hover:opacity-70 transition-opacity"
                                style={{
                                    fontFamily: "'Georama SemiExpanded', 'Georama', sans-serif",
                                    fontWeight: 400,
                                    fontStyle: 'normal',
                                    fontSize: '12px',
                                    lineHeight: '18px',
                                    letterSpacing: '0.04em',
                                }}>
                                <span>My Account</span>
                                <ChevronRight className="size-4 text-[#000000] shrink-0" strokeWidth={1} />
                            </NavLink>

                            <NavLink
                                to={routes.accountOrders}
                                onClick={context.close}
                                className="flex items-center justify-between text-[#000000] hover:opacity-70 transition-opacity"
                                style={{
                                    fontFamily: "'Georama SemiExpanded', 'Georama', sans-serif",
                                    fontWeight: 400,
                                    fontStyle: 'normal',
                                    fontSize: '12px',
                                    lineHeight: '18px',
                                    letterSpacing: '0.04em',
                                }}>
                                <span>My Orders</span>
                                <ChevronRight className="size-4 text-[#000000] shrink-0" strokeWidth={1} />
                            </NavLink>

                            <button
                                type="button"
                                onClick={() => setLogoutConfirmOpen(true)}
                                className="w-full flex items-center justify-between text-[#000000] hover:opacity-70 transition-opacity text-left cursor-pointer"
                                style={{
                                    fontFamily: "'Georama SemiExpanded', 'Georama', sans-serif",
                                    fontWeight: 400,
                                    fontStyle: 'normal',
                                    fontSize: '12px',
                                    lineHeight: '18px',
                                    letterSpacing: '0.04em',
                                }}>
                                <span>Logout</span>
                            </button>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-6 w-full">
                            <NavLink
                                to={routes.login}
                                onClick={context.close}
                                className="flex items-center justify-between text-[#000000] hover:opacity-70 transition-opacity"
                                style={{
                                    fontFamily: "'Georama SemiExpanded', 'Georama', sans-serif",
                                    fontWeight: 400,
                                    fontStyle: 'normal',
                                    fontSize: '12px',
                                    lineHeight: '18px',
                                    letterSpacing: '0.04em',
                                }}>
                                <span>{t('signIn', 'Sign In')}</span>
                                <ChevronRight className="size-4 text-[#000000] shrink-0" strokeWidth={1} />
                            </NavLink>
                        </div>
                    )}
                </nav>
            </div>

            {isAuthenticated && (
                <LogoutConfirmDialog open={logoutConfirmOpen} onOpenChange={setLogoutConfirmOpen} />
            )}
        </>
    );
}

/**
 * ResponsiveNavigationMenu - A unified responsive navigation component
 *
 * This component uses CSS and Tailwind to adapt the same navigation structure
 * for both mobile and desktop:
 * - On mobile (< 1024px): Hamburger button with expandable vertical menu
 * - On desktop (>= 1024px): Horizontal mega menu with dropdown navigation
 *
 * The component renders a single navigation structure with responsive classes
 * controlling layout, visibility, and interaction patterns. This minimizes DOM bloat
 * while maintaining SSR compatibility.
 *
 * @param props - Component props
 * @param props.resolve - Promise resolving to root categories and first-level subcategories
 * @param props.defer - Promise resolving to deeper subcategory data for prefetch
 * @returns A responsive navigation component with CSS-controlled responsive behavior
 */
export default function ResponsiveNavigationMenu({
    resolve,
    defer,
}: ComponentPropsWithoutRef<typeof WithCategoryNavigationMenu>): ReactElement {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const { t } = useTranslation('header');
    const navigate = useNavigate();

    const defaultListStyle = {
        width: '100%',
        maxWidth: '100%',
    };

    // Handler for top-level category clicks
    const handleTopLevelClick = useCallback(
        (categoryId: string) => {
            void navigate(routeHref(routes.category, { categoryId }));
        },
        [navigate]
    );

    // Element props generator
    const getElementProps = useCallback(
        ({
            level,
            category,
            isLeaf,
        }: {
            level: number;
            category: ShopperProducts.schemas['Category'];
            isLeaf?: boolean;
        }) => {
            const isSubcategory = level >= 1;
            const isClickableParent = level === 0 && !isLeaf && category.id;

            return {
                className: cn(
                    'header-category-link font-georama font-normal not-italic text-[12px] leading-[18px] tracking-[0.04em] text-[#000000]',
                    isSubcategory &&
                        'hover:!bg-transparent focus:!bg-transparent transition-colors pt-4 !pb-0 pb-0 !pl-0 pl-0'
                ),
                ...(isClickableParent && {
                    // Use onPointerDown instead of onClick for mouse-only navigation.
                    // This preserves keyboard accessibility: Enter/Space on the trigger
                    // expands the dropdown (Radix behavior), while mouse clicks navigate
                    // to the category page. Without this guard, keyboard users would be
                    // forced to navigate without being able to explore subcategories.
                    onPointerDown: (e: React.PointerEvent) => {
                        if (e.pointerType === 'mouse') {
                            handleTopLevelClick(category.id);
                        }
                    },
                }),
            };
        },
        [handleTopLevelClick]
    );

    return (
        <WithCategoryNavigationMenu resolve={resolve} defer={defer}>
            {({ categories }) => {
                const mobileMenuContext: MobileMenuContextType = {
                    isOpen: mobileMenuOpen,
                    toggle: () => setMobileMenuOpen(!mobileMenuOpen),
                    close: () => setMobileMenuOpen(false),
                    categories,
                };

                return (
                    <MobileMenuContext.Provider value={mobileMenuContext}>
                        {/* Mobile: Hamburger button */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={mobileMenuContext.toggle}
                            data-mobile-menu-trigger="true"
                            data-state={mobileMenuOpen ? 'open' : 'closed'}
                            className="lg:hidden hover:bg-transparent hover:opacity-50 transition-opacity"
                            aria-label={mobileMenuOpen ? t('closeMenu', 'Close menu') : t('openMenu', 'Open menu')}
                            aria-expanded={mobileMenuOpen}>
                            {mobileMenuOpen ? (
                                <X className="size-5" />
                            ) : (
                                <img
                                    src={hamburgerIcon}
                                    alt={t('openMenu', 'Open menu')}
                                    className="h-7 w-7 lg:h-4 w-auto [filter:var(--header-logo-filter)]"
                                />
                            )}
                        </Button>

                        {/* Desktop: Mega menu (always rendered, hidden on mobile with CSS) */}
                        <div className="hidden lg:flex items-center h-full">
                            <CategoryNavigationMenu
                                categories={categories}
                                delayDuration={0}
                                propsViewport={() => ({
                                    className:
                                        'rounded-none border-0 shadow-lg [&[data-state=open]]:animate-[menuSlideDown_0.15s_ease-in] [&[data-state=closed]]:animate-none will-change-transform !mt-0',
                                    // Anchor the fixed panel to both viewport edges via `left: 0` + `right: 0`
                                    // so its width matches the layout viewport, *excluding* the scrollbar gutter.
                                    // Using `width: 100vw` instead would include the scrollbar and overshoot
                                    // any in-flow content (e.g. the header) by the scrollbar's width.
                                    style: {
                                        position: 'fixed',
                                        top: 'var(--header-height)',
                                        left: 0,
                                        right: 0,
                                    },
                                })}
                                propsContentContainer={() => ({
                                    className:
                                        '!p-0 !left-auto !right-auto !w-full md:!w-full !animate-none !transition-none',
                                })}
                                propsContent={({ category }) => ({
                                    className: cn(
                                        'section-container pt-0 pb-6 max-w-none md:pl-16 md:pr-6',
                                        hasBanner(category) &&
                                            (isVertical(category)
                                                ? 'grid md:grid-cols-[1fr_.3fr] items-start'
                                                : 'grid md:grid-cols-[1fr_.6fr] items-start')
                                    ),
                                })}
                                propsList={({ parent, categories: subCategories, level }) => {
                                    if (level === 1) {
                                        if (isVertical(parent)) {
                                            return {
                                                style: { width: '200px' },
                                                className: 'flex flex-col p-0',
                                            };
                                        }
                                        return {
                                            style: {
                                                ...defaultListStyle,
                                                gridTemplateColumns: `repeat(${subCategories.length}, minmax(0, 1fr))`,
                                            },
                                            className: 'grid p-0',
                                        };
                                    }
                                }}
                                propsListItem={({ level }) =>
                                    level === 1 ? { className: 'mega-menu-l1-item' } : undefined
                                }
                                propsElement={getElementProps}
                                renderSlotListAfter={({ level, parent }) => {
                                    if (level === 1 && hasBanner(parent)) {
                                        return (
                                            <aside className="self-stretch w-full h-full flex items-stretch justify-end pt-4">
                                                <CategoryBanner category={parent} />
                                            </aside>
                                        );
                                    }
                                }}
                            />
                        </div>

                        {/* Mobile: Menu dropdown (rendered here to be inside provider) */}
                        <MobileMenuDropdown />
                    </MobileMenuContext.Provider>
                );
            }}
        </WithCategoryNavigationMenu>
    );
}
